// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect, useCallback, useRef } from 'react'
import { Loader2, Sparkles, ShieldCheck } from 'lucide-react'
import SearchableSelect from '../../components/common/SearchableSelect'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { carryWaiver, hasDataGap, isBaselineExempt } from '../../components/carerx/carePlanGaps'
import { formatUsDateLocal } from '../../api/format'
import DateInput from '../../components/ui/DateInput'
import { useRefreshReviewAssignment } from '../../hooks/queries/carerxClinical'
import CarePlanAssignee from './CarePlanAssignee'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { GOAL_FIELD_LIMITS, goalFieldViolation, type LimitedGoalField } from './carePlanGoalLimits'
import { clearCarePlanDraft, draftDiffers, readCarePlanDraft, writeCarePlanDraft, type CarePlanDraftGoal } from './carePlanDraft'

// D-CRX-45 Phase 5 — the CR-E9 Consultation + CR-E10 Care Plan panels, wired to the persistence +
// engine endpoints. The live model generation is gated behind the Anthropic BAA key (server returns
// ai_not_configured until it's set); the pharmacist can always author/edit and save. The status +
// data-gap engines run server-side (evaluate endpoint) and drive eligibility + the save block.

const card: React.CSSProperties = { border: '1px solid #dde5ec', borderRadius: 12, padding: 15, marginTop: 14 }
const lab: React.CSSProperties = { fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase', color: '#8496a5', fontWeight: 700 }
const inp: React.CSSProperties = { fontSize: 12.5, padding: '5px 8px', border: '1px solid #dde5ec', borderRadius: 6, background: '#fff' }
const fld: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3 }
const btnP: React.CSSProperties = { ...inp, cursor: 'pointer', background: '#185FA5', color: '#fff', border: 'none', fontWeight: 640 }
const area: React.CSSProperties = { ...inp, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }

/** ASP 2026-10-28 (#8): the panel's message colour comes from an explicit kind, never the text. */
type PanelMsg = { text: string; kind: 'success' | 'error' }
const msgColor = (m: PanelMsg) => (m.kind === 'success' ? '#0d6b52' : '#b23a48')
/** #8: shown beside a Save button while the panel holds edits the server has not got. */
function UnsavedHint() {
  return <span role="status" style={{ marginLeft: 10, fontSize: 11.5, color: '#92610f', fontWeight: 600 }}>Unsaved changes</span>
}
/** The evaluate call (and its global error toast on a 5xx) waits for typing to pause (#8). */
export const EVAL_DEBOUNCE_MS = 500

// ── CR-E9 Consultation ──
/** P0 B10: what a panel shows instead of an editable form when its record could not be read. */
function LoadFailed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" style={{ fontSize: 12.5, color: '#7f1d1d', background: '#fef2f2', border: '1px solid #f0a8a8', borderRadius: 8, padding: '8px 11px' }}>
      Could not load this record, so it cannot be edited here — saving now could overwrite what is stored. {message}{' '}
      <button type="button" onClick={onRetry} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 7, border: '1px solid #dde5ec', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
    </div>
  )
}

type Consult = { person_consulted: string; status: string; follow_up_on: string; note: string; decline_reason: string }
const emptyConsult = (): Consult => ({ person_consulted: 'patient', status: 'pending', follow_up_on: '', note: '', decline_reason: '' })

function ConsultationPanel({ therapyId, needed, onDirty }: { therapyId: string; needed: boolean | null; onDirty: (dirty: boolean) => void }) {
  const [c, setC] = useState<Consult>(emptyConsult)
  /** #8: the consultation as last loaded or saved; anything else on screen is unsaved. */
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  // P0 B10: a failed load used to fall through to an empty form that Save could post over the real record.
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [attested, setAttested] = useState(false)
  const [msg, setMsg] = useState<PanelMsg | null>(null)
  const [sug, setSug] = useState<api.ClinicalSuggestion[] | null>(null)
  const [sugBusy, setSugBusy] = useState(false)
  const [sugMsg, setSugMsg] = useState<string | null>(null)
  const [inserted, setInserted] = useState<Record<number, boolean>>({})

  useEffect(() => {
    setLoaded(false); setLoadErr(null)
    api.getConsultation(therapyId).then(d => {
      const next: Consult = d.consultation ? {
        person_consulted: d.consultation.person_consulted || 'patient',
        status: d.consultation.status || 'pending',
        follow_up_on: d.consultation.follow_up_on ? String(d.consultation.follow_up_on).slice(0, 10) : '',
        note: d.consultation.note || '', decline_reason: d.consultation.decline_reason || '',
      } : emptyConsult()
      setC(next); setSavedKey(JSON.stringify(next))
    }).catch(e => setLoadErr(friendlyError(e))).finally(() => setLoaded(true))
  }, [therapyId, attempt])

  const dirty = loaded && !loadErr && savedKey !== null && JSON.stringify(c) !== savedKey
  useEffect(() => { onDirty(dirty) }, [dirty, onDirty])

  if (needed === false) {
    return <section style={card}><div style={{ fontSize: 13, fontWeight: 660, marginBottom: 4 }}>Consultation</div>
      <div style={{ fontSize: 12.5, color: '#8496a5' }}>No consultation needed (set at enrollment).</div></section>
  }

  // What this consultation is still missing, in the same words the server uses (it is the real
  // gate — this only saves the round trip and tells the pharmacist before they click).
  const consultBlocker = (): string | null => {
    if (c.status === 'declined') return c.decline_reason.trim() ? null : 'A declined consultation needs the reason it was declined.'
    if (!c.note.trim()) return 'A consultation needs counselling notes before it can be saved.'
    if (c.status === 'completed' && !attested) return 'Completing a consultation requires the pharmacist attestation.'
    return null
  }
  const save = async () => {
    const blocker = consultBlocker()
    if (blocker) { setMsg({ text: blocker, kind: 'error' }); return }
    const posted = JSON.stringify(c)
    try {
      await api.saveConsultation(therapyId, { ...c, note_provenance: 'pharmacist_authored', attested })
      setSavedKey(posted)
      const m: PanelMsg = { text: 'Saved.', kind: 'success' }
      setMsg(m); setTimeout(() => setMsg(cur => (cur === m ? null : cur)), 1500)
    } catch (e) { setMsg({ text: friendlyError(e), kind: 'error' }) }
  }

  return (
    <section style={card}>
      <div style={{ fontSize: 13, fontWeight: 660, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Sparkles size={15} color="#185FA5" /> Consultation <span style={{ fontSize: 10.5, color: '#8496a5', fontWeight: 500 }}>(CR-E9)</span>
      </div>
      {!loaded ? <Loader2 className="spin" size={16} /> : loadErr ? <LoadFailed message={loadErr} onRetry={() => setAttempt(a => a + 1)} /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 10 }}>
            <div style={fld}><label style={lab}>Person consulted</label>
              <SearchableSelect value={c.person_consulted} onChange={(v: string) => setC(x => ({ ...x, person_consulted: v }))}
                options={[{ value: 'patient', label: 'Patient' }, { value: 'caregiver', label: 'Caregiver' }, { value: 'nurse', label: 'Nurse' }, { value: 'prescriber_office', label: 'Prescriber office' }]} />
            </div>
            <div style={fld}><label style={lab}>Status</label>
              <SearchableSelect value={c.status} onChange={(v: string) => setC(x => ({ ...x, status: v }))}
                options={[{ value: 'pending', label: 'Pending' }, { value: 'pending_callback', label: 'Pending call back' }, { value: 'completed', label: 'Completed' }, { value: 'declined', label: 'Declined' }]} />
            </div>
            {c.status === 'pending_callback' && (
              <div style={fld}><label style={lab}>Follow-up date (CR-V1)</label>
                <DateInput label="Follow-up date" value={c.follow_up_on} onChange={v => setC(x => ({ ...x, follow_up_on: v ?? '' }))} style={inp} /></div>
            )}
            {c.status === 'declined' && (
              <div style={fld}><label style={lab}>Decline reason</label>
                <input value={c.decline_reason} onChange={e => setC(x => ({ ...x, decline_reason: e.target.value }))} style={inp} /></div>
            )}
          </div>
          {/* D-CRX-60: what to counsel on, derived from this drug's record + assigned protocols.
              Selecting a point writes it into the note; the pharmacist edits and owns the text. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <button onClick={async () => {
              setSugBusy(true); setSugMsg(null)
              try { const r = await api.suggestConsultationPoints(therapyId); setSug(r.suggestions); if (!r.suggestions.length) setSugMsg('No suggestions — this drug record has no clinical configuration yet.') }
              catch (e) { setSugMsg(friendlyError(e)) } finally { setSugBusy(false) }
            }} style={{ ...inp, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {sugBusy ? <Loader2 className="spin" size={13} /> : <Sparkles size={13} />} Suggest counseling points
            </button>
            {sug && <span style={{ fontSize: 11, color: '#8496a5' }}>{sug.length} suggested · click one to add it to the note</span>}
            {sugMsg && <span style={{ fontSize: 11.5, color: '#b23a48' }}>{sugMsg}</span>}
          </div>
          {sug && sug.length > 0 && (
            <div style={{ border: '1px solid #dde5ec', borderRadius: 9, padding: 9, marginBottom: 8, maxHeight: 260, overflowY: 'auto' }}>
              {sug.map((s, i) => (
                <div key={i} style={{ borderBottom: i < sug.length - 1 ? '1px solid #eef2f5' : 'none', padding: '6px 2px' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#185FA5', whiteSpace: 'nowrap' }}>{s.topic}</span>
                    <span style={{ fontSize: 12.5, flex: 1 }}>{s.text}</span>
                    <button onClick={() => {
                      setC(x => ({ ...x, note: (x.note ? x.note.replace(/\s*$/, '') + '\n' : '') + '• ' + (s.text ?? '') }))
                      setInserted(v => ({ ...v, [i]: true }))
                    }} style={{ ...inp, cursor: 'pointer', fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap',
                      background: inserted[i] ? '#e9f7ec' : '#fff', color: inserted[i] ? '#0d6b52' : '#16232e' }}>
                      {inserted[i] ? '✓ Added' : 'Add to note'}</button>
                  </div>
                  <div style={{ fontSize: 10.5, color: '#8496a5', fontStyle: 'italic', marginTop: 2 }}>Basis: {s.basis}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#8496a5', marginBottom: 6 }}>Suggestions are derived from this drug's record and assigned protocols — each names its basis. They are candidates: the note you save is yours, authored and attested by you.</div>
          <textarea value={c.note} onChange={e => setC(x => ({ ...x, note: e.target.value }))} placeholder="Counseling notes for the patient record…" aria-label="Counseling notes" style={{ ...inp, width: '100%', minHeight: 70 }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, margin: '10px 0' }}>
            <input type="checkbox" checked={attested} onChange={e => setAttested(e.target.checked)} />
            I attest I reviewed this consultation with the patient/caregiver.
          </label>
          <div><button onClick={save} disabled={!!consultBlocker()} title={consultBlocker() ?? undefined}
            style={{ ...btnP, opacity: consultBlocker() ? .5 : 1, cursor: consultBlocker() ? 'not-allowed' : 'pointer' }}>Save consultation</button>
            {dirty && <UnsavedHint />}
            {msg && <span role={msg.kind === 'error' ? 'alert' : undefined} style={{ marginLeft: 10, fontSize: 12, color: msgColor(msg) }}>{msg.text}</span>}</div>
        </>
      )}
    </section>
  )
}

// ── CR-E10 Care Plan ──
type GoalFields = {
  horizon: string; domain: string; goal_statement: string; measure: string; unit: string; direction: string;
  baseline_value: string; baseline_date: string; target_value: string; target_date: string; status: string;
}
/** `key` is a client-only identity: React's row key and what a gap waiver is held against, so removing
 *  or editing another goal cannot move a waiver onto the wrong one. It is never sent. `id` / `prior` are
 *  the saved goal this row continues (sent as `id` so the server can carry its saved waiver). */
type GoalDraft = GoalFields & { key: string; id?: string; prior?: { measure: string; direction: string } }
/** A baseline waiver on screen: `persisted` came from the saved plan (who / when stamped by the server). */
export type WaiverState = { reason: string; persisted: boolean; by_name?: string | null; at?: string | null }
let goalSeq = 0
const newGoalKey = () => `g${++goalSeq}`
const emptyGoal = (): GoalDraft => ({
  key: newGoalKey(),
  horizon: 'short_term', domain: 'clinical', goal_statement: '', measure: '', unit: '', direction: 'down',
  baseline_value: '', baseline_date: '', target_value: '', target_date: '', status: 'unscored',
})
const toWire = (g: GoalDraft): GoalFields => { const { key, id, prior, ...fields } = g; void key; void id; void prior; return fields }
/** The waiver that applies to a goal now: a saved one lapses once its measure or direction changes. */
export const effectiveWaiver = (g: GoalDraft, waivers: Record<string, WaiverState>): WaiverState | null => {
  const w = waivers[g.key]
  if (!w) return null
  if (w.persisted && g.prior && !carryWaiver(g.prior, g)) return null
  return w
}
const planKey = (planType: string, goals: GoalDraft[], waivers: Record<string, WaiverState>) =>
  JSON.stringify({ planType, goals: goals.map(g => ({ ...toWire(g), waiver: effectiveWaiver(g, waivers)?.reason ?? null })) })
/** #14: waivers go on the wire keyed by the goal's index in the same request (the server's goal id).
 *  `onlyFresh` (the save): a saved waiver is carried by the server from the goal's id, not re-sent. */
export const wireWaivers = (goals: GoalDraft[], waivers: Record<string, WaiverState>, onlyFresh = false): api.CarePlanGapWaiver[] =>
  goals.flatMap((g, i) => {
    const w = effectiveWaiver(g, waivers)
    return w && w.reason.trim() && !(onlyFresh && w.persisted) ? [{ goal_id: String(i), reason: w.reason.trim() }] : []
  })
/** The plan as the GET returned it: drafts plus the saved waivers, pre-filled so a reopened plan is not blocked. */
export function draftsFromPlan(rows: api.CarePlanGoalRec[]): { goals: GoalDraft[]; waivers: Record<string, WaiverState> } {
  const waivers: Record<string, WaiverState> = {}
  const goals = rows.map(g => {
    const d: GoalDraft = {
      key: newGoalKey(), id: g.id, prior: { measure: g.measure || '', direction: g.direction || 'down' },
      horizon: g.horizon || 'short_term', domain: g.domain || 'clinical', goal_statement: g.goal_statement || '',
      measure: g.measure || '', unit: g.unit || '', direction: g.direction || 'down',
      baseline_value: g.baseline_value || '', baseline_date: g.baseline_date ? String(g.baseline_date).slice(0, 10) : '',
      target_value: g.target_value || '', target_date: g.target_date ? String(g.target_date).slice(0, 10) : '',
      status: g.status || 'unscored',
    }
    if (g.baseline_waiver?.reason) waivers[d.key] = { reason: g.baseline_waiver.reason, persisted: true, by_name: g.baseline_waiver.by_name, at: g.baseline_waiver.at }
    return d
  })
  return { goals, waivers }
}
/** #1026 draft + #14 waivers: the draft keeps each goal's saved id and its unsaved waiver by position, so a
 *  restored draft still continues the saved goals (and carries their saved waivers) instead of dropping them. */
const draftExtras = (goals: GoalDraft[], waivers: Record<string, WaiverState>) => ({
  ids: goals.map(g => g.id ?? null),
  waivers: goals.map(g => { const w = waivers[g.key]; return w && !w.persisted ? w.reason : null }),
})
export function restoreDraft(rows: CarePlanDraftGoal[], ids: (string | null)[] | undefined, fresh: (string | null)[] | undefined,
  server: GoalDraft[], serverWaivers: Record<string, WaiverState>): { goals: GoalDraft[]; waivers: Record<string, WaiverState> } {
  const byId = new Map(server.filter(g => g.id).map(g => [g.id!, g]))
  const waivers: Record<string, WaiverState> = {}
  const goals = rows.map((row, i) => {
    const id = ids?.[i]
    const saved = id ? byId.get(id) : undefined
    const d: GoalDraft = { ...row, key: newGoalKey(), id: saved?.id, prior: saved?.prior }
    const reason = fresh?.[i]
    if (reason) waivers[d.key] = { reason, persisted: false }
    else if (saved && serverWaivers[saved.key]) waivers[d.key] = serverWaivers[saved.key]
    return d
  })
  return { goals, waivers }
}
/** "Baseline waived: <reason> — <name>, <date>"; a waiver not yet saved says so instead. */
function WaiverLine({ w }: { w: WaiverState }) {
  const who = [w.by_name, w.at ? formatUsDateLocal(w.at) : ''].filter(Boolean).join(', ')
  return <span><b>Baseline waived:</b> {w.reason}{w.persisted ? (who ? ` — ${who}` : '') : ' (not saved yet)'}</span>
}

function CarePlanPanel({ therapyId, onDirty, hideAssignee = false }: { therapyId: string; onDirty: (dirty: boolean) => void; hideAssignee?: boolean }) {
  const [goals, setGoals] = useState<GoalDraft[]>([])
  const [planType, setPlanType] = useState('initial')
  /** #8: the plan as last loaded or saved; anything else on screen is unsaved. */
  const [savedKey, setSavedKey] = useState<string | null>(null)
  /** #14: per-goal baseline waivers (goal key → reason; saved ones also carry who/when). */
  const [waivers, setWaivers] = useState<Record<string, WaiverState>>({})
  const [waiveFor, setWaiveFor] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  // P0 B10: a failed load used to fall through to an empty form that Save could post over the real record.
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [attested, setAttested] = useState(false)
  const [msg, setMsg] = useState<PanelMsg | null>(null)
  const [evalOut, setEvalOut] = useState<{ gaps: { goalId: string; reason: string }[]; save_blocked: boolean; goals: { id: string; eligible: string[]; derived_status: string }[] } | null>(null)
  const [gSug, setGSug] = useState<api.ClinicalSuggestion[] | null>(null)
  const [gSugBusy, setGSugBusy] = useState(false)
  const [gSugMsg, setGSugMsg] = useState<string | null>(null)
  const [gAccepted, setGAccepted] = useState<Record<number, boolean>>({})
  const refreshAssignment = useRefreshReviewAssignment()
  // Ticket #1026 auto-save: unsaved edits are written to this tab's session storage as the clinician
  // types and restored on return, so a failed save or a reload no longer costs the whole plan.
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null)
  const [restoredFrom, setRestoredFrom] = useState<string | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const applyPlan = useCallback((d: Awaited<ReturnType<typeof api.getCarePlanE10>>) => {
    const { goals: gs, waivers: ws } = draftsFromPlan(d.goals ?? [])
    const pt = d.plan?.plan_type || 'initial'
    setGoals(gs); setWaivers(ws); setPlanType(pt); setSavedKey(planKey(pt, gs, ws))
    return { goals: gs, waivers: ws, planType: pt }
  }, [])
  useEffect(() => {
    setLoaded(false); setLoadErr(null); setRestoredFrom(null); setDraftSavedAt(null)
    api.getCarePlanE10(therapyId).then(d => {
      // savedKey stays the SERVER's plan, so a restored draft reads as unsaved until it is saved.
      const server = applyPlan(d)
      const draft = readCarePlanDraft(therapyId)
      if (draft && draftDiffers(draft, server.goals.map(toWire), server.planType)) {
        const restored = restoreDraft(draft.goals, draft.ids, draft.waivers, server.goals, server.waivers)
        setGoals(restored.goals); setWaivers(restored.waivers); setPlanType(draft.planType); setAttested(draft.attested)
        setRestoredFrom(draft.savedAt); setDraftSavedAt(draft.savedAt)
        return
      }
      if (draft) clearCarePlanDraft(therapyId)
      setAttested(false)
    }).catch(e => setLoadErr(friendlyError(e))).finally(() => setLoaded(true))
  }, [therapyId, attempt, applyPlan])

  const dirty = loaded && !loadErr && savedKey !== null && planKey(planType, goals, waivers) !== savedKey
  useEffect(() => { onDirty(dirty) }, [dirty, onDirty])

  useEffect(() => {
    if (!loaded || !dirty) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      const w = writeCarePlanDraft(therapyId, { planType, goals: goals.map(toWire), attested, ...draftExtras(goals, waivers) })
      if (w) setDraftSavedAt(w.savedAt)
    }, 400)
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current) }
  }, [loaded, dirty, therapyId, planType, goals, attested, waivers])

  const discardDraft = () => {
    clearCarePlanDraft(therapyId); setRestoredFrom(null); setDraftSavedAt(null)
    setAttempt(a => a + 1)
  }

  const evalGoals = useCallback(async (gs: GoalDraft[], ws: Record<string, WaiverState>) => {
    try {
      const out = await api.evaluateCarePlan(therapyId, {
        goals: gs.map((g, i) => ({
          id: String(i), direction: g.direction, domain: g.domain, baseline_value: g.baseline_value,
          target_value: g.target_value ? Number(g.target_value) : null,
          target_date: g.target_date || undefined,
          values: g.baseline_value ? [{ value: Number(g.baseline_value), date: g.baseline_date || new Date().toISOString().slice(0, 10) }] : [],
        })),
        waivers: wireWaivers(gs, ws),
      })
      setEvalOut(out)
    } catch { /* eval is advisory in UI */ }
  }, [therapyId])

  // #8: debounced — this fired on every keystroke, and each transient 5xx raised a global error toast.
  const evalInput = useDebouncedValue(goals, EVAL_DEBOUNCE_MS)
  useEffect(() => {
    if (!loaded) return
    if (evalInput.length) evalGoals(evalInput, waivers); else setEvalOut(null)
  }, [loaded, evalInput, waivers, evalGoals])

  const setGoal = (i: number, patch: Partial<GoalFields>) => setGoals(gs => gs.map((g, idx) => idx === i ? { ...g, ...patch } : g))
  const addGoal = () => setGoals(gs => [...gs, emptyGoal()])
  const removeGoal = (i: number) => setGoals(gs => gs.filter((_, idx) => idx !== i))
  const violation = goalFieldViolation(goals)

  // #14: the same rule the server applies (carePlanGaps ↔ DataGapEngine), read live so the gap list and
  // the Save gate never lag the debounced evaluate. The server re-derives it on save regardless.
  const openGaps = goals.map((g, i) => ({ g, i })).filter(({ g }) => hasDataGap(g) && !effectiveWaiver(g, waivers))
  const gapBlocked = openGaps.length > 0

  // Same shape as the consultation: name what is missing rather than posting an empty plan.
  const planBlocker = (): string | null => {
    if (goals.length === 0) return 'A care plan needs at least one goal before it can be saved.'
    if (goals.some(g => !g.goal_statement.trim())) return 'Every goal needs a statement — remove the blank rows or fill them in.'
    if (violation) return violation.message
    return null
  }
  const save = async () => {
    const blocker = planBlocker()
    if (blocker) { setMsg({ text: blocker, kind: 'error' }); return }
    if (gapBlocked) { setMsg({ text: 'Resolve or waive data gaps before saving.', kind: 'error' }); return }
    const posted = planKey(planType, goals, waivers)
    try {
      const r = await api.saveCarePlanE10(therapyId, {
        plan_type: planType,
        goals: goals.map(g => ({ ...toWire(g), id: g.id, provenance: 'pharmacist_authored',
          // An undone saved waiver is not carried. This can only remove a waiver; the server stamps the rest.
          keep_baseline_waiver: !!effectiveWaiver(g, waivers)?.persisted })),
        waivers: wireWaivers(goals, waivers, true),
        attested,
      })
      setSavedKey(posted)
      // The server holds the plan now; the local draft has done its job.
      clearCarePlanDraft(therapyId); setDraftSavedAt(null); setRestoredFrom(null)
      // Re-read quietly so the new version's goal ids and server-stamped waivers (who / when) are on screen.
      api.getCarePlanE10(therapyId).then(applyPlan).catch(() => { /* the save stands; a reopen re-reads */ })
      if (r.auto_assigned) refreshAssignment(therapyId)
      const m: PanelMsg = { text: r.auto_assigned ? 'Care plan saved. You are now its pharmacist.' : 'Care plan saved.', kind: 'success' }
      setMsg(m); setTimeout(() => setMsg(cur => (cur === m ? null : cur)), 2000)
    } catch (e) { setMsg({ text: friendlyError(e), kind: 'error' }) }
  }
  const fmtTime = (iso: string) => { try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) } catch { return iso } }
  // Character counter for the bounded fields: quiet until the clinician is near the limit.
  const counter = (field: LimitedGoalField, value: string) => {
    const limit = GOAL_FIELD_LIMITS[field]
    if (value.length < limit - 15) return null
    const over = value.length > limit
    return <span style={{ fontSize: 10, color: over ? '#b23a48' : value.length === limit ? '#9a6b00' : '#8496a5', textAlign: 'right' }}>
      {value.length}/{limit}{over ? ' — too long' : ''}</span>
  }
  // A restored draft or an accepted AI suggestion can carry text longer than the input now allows;
  // outline that field so the message under Save points at something visible.
  const boxFor = (field: LimitedGoalField, value: string): React.CSSProperties =>
    value.length > GOAL_FIELD_LIMITS[field] ? { ...inp, border: '1px solid #b23a48', background: '#fff5f6' } : inp

  return (
    <section style={card}>
      <div style={{ fontSize: 13, fontWeight: 660, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck size={15} color="#185FA5" /> Care plan <span style={{ fontSize: 10.5, color: '#8496a5', fontWeight: 500 }}>(CR-E10)</span>
      </div>
      {!hideAssignee && <div style={{ marginBottom: 8 }}><CarePlanAssignee therapyId={therapyId} /></div>}
      {!loaded ? <Loader2 className="spin" size={16} /> : loadErr ? <LoadFailed message={loadErr} onRetry={() => setAttempt(a => a + 1)} /> : (
        <>
          {restoredFrom && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, padding: '7px 10px', marginBottom: 8, borderRadius: 8, background: '#fff8e6', border: '1px solid #f3d99b', color: '#6b4e00' }}>
              <span style={{ flex: 1 }}>Restored your unsaved goals from {fmtTime(restoredFrom)}. They are not saved to the care plan until you click Save care plan.</span>
              <button onClick={discardDraft} style={{ ...inp, cursor: 'pointer', fontSize: 11 }}>Discard and reload</button>
            </div>
          )}
          <div style={{ fontSize: 11, color: '#8496a5', marginBottom: 8 }}>SMART goals; the status engine (missing data = not met) and data-gap engine run server-side.</div>

          {/* D-CRX-60: candidate goals derived from this therapy's drug record + assigned protocols. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <button onClick={async () => {
              setGSugBusy(true); setGSugMsg(null)
              try { const r = await api.suggestCarePlanGoals(therapyId); setGSug(r.suggestions); if (!r.suggestions.length) setGSugMsg('No suggestions — this drug record has no clinical configuration yet.') }
              catch (e) { setGSugMsg(friendlyError(e)) } finally { setGSugBusy(false) }
            }} style={{ ...inp, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {gSugBusy ? <Loader2 className="spin" size={13} /> : <Sparkles size={13} />} Suggest goals
            </button>
            {gSug && <span style={{ fontSize: 11, color: '#8496a5' }}>{gSug.length} candidates · accept the ones that fit this patient, then edit</span>}
            {gSugMsg && <span style={{ fontSize: 11.5, color: '#b23a48' }}>{gSugMsg}</span>}
          </div>
          {gSug && gSug.length > 0 && (
            <div style={{ border: '1px solid #dde5ec', borderRadius: 9, padding: 9, marginBottom: 10 }}>
              {gSug.map((s, i) => (
                <div key={i} style={{ borderBottom: i < gSug.length - 1 ? '1px solid #eef2f5' : 'none', padding: '7px 2px' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#185FA5', whiteSpace: 'nowrap', textTransform: 'uppercase' }}>{s.domain}</span>
                    <span style={{ fontSize: 12.5, flex: 1 }}>{s.goal_statement}</span>
                    <button onClick={() => {
                      setGoals(gs => [...gs, { ...emptyGoal(),
                        horizon: s.horizon || 'short_term', domain: s.domain || 'clinical',
                        goal_statement: s.goal_statement || '', measure: s.measure || '', unit: s.unit || '',
                        direction: s.direction || 'binary', target_value: s.target_value || '' }])
                      setGAccepted(v => ({ ...v, [i]: true }))
                    }} style={{ ...inp, cursor: 'pointer', fontSize: 11, padding: '2px 8px', whiteSpace: 'nowrap',
                      background: gAccepted[i] ? '#e9f7ec' : '#fff', color: gAccepted[i] ? '#0d6b52' : '#16232e' }}>
                      {gAccepted[i] ? '✓ Accepted' : 'Accept'}</button>
                  </div>
                  <div style={{ fontSize: 10.5, color: '#8496a5', marginTop: 2 }}>
                    {s.measure ? <span>Measure: {s.measure}{s.target_value ? ` · target ${s.target_value}${s.unit || ''}` : ''} · </span> : null}
                    <span style={{ fontStyle: 'italic' }}>Basis: {s.basis}</span>
                  </div>
                  {s.why && <div style={{ fontSize: 10.5, color: '#8496a5', marginTop: 1 }}>{s.why}</div>}
                </div>
              ))}
            </div>
          )}

          {gapBlocked && (
            <div style={{ border: '1px solid #f0a0a0', background: '#fbecee', borderRadius: 9, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#b23a48' }}>⚠ Data gaps ({openGaps.length}) — save is blocked until each is resolved or waived</div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 11.5, color: '#41566a' }}>
                {openGaps.map(({ g, i }) => <li key={g.key}>Goal {i + 1}: baseline value missing — enter it, or waive it on the goal.</li>)}
              </ul>
            </div>
          )}

          {goals.map((g, i) => (
            <div key={g.key} style={{ border: '1px solid #dde5ec', borderRadius: 9, padding: 11, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>Goal {i + 1}{evalOut?.goals[i] ? ` · derived: ${evalOut.goals[i].derived_status}` : ''}</div>
                <button onClick={() => removeGoal(i)} style={{ ...inp, cursor: 'pointer', fontSize: 11 }}>Remove</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 8 }}>
                <div style={fld}><label style={lab}>Horizon</label>
                  <SearchableSelect value={g.horizon} onChange={(v: string) => setGoal(i, { horizon: v })} options={[{ value: 'short_term', label: 'Short term' }, { value: 'long_term', label: 'Long term' }]} /></div>
                <div style={fld}><label style={lab}>Domain</label>
                  <SearchableSelect value={g.domain} onChange={(v: string) => setGoal(i, { domain: v })} options={[{ value: 'clinical', label: 'Clinical' }, { value: 'safety', label: 'Safety' }, { value: 'adherence', label: 'Adherence' }, { value: 'self_management', label: 'Self-management' }]} /></div>
                <div style={fld}><label style={lab}>Direction</label>
                  <SearchableSelect value={g.direction} onChange={(v: string) => setGoal(i, { direction: v })} options={[{ value: 'down', label: 'Down' }, { value: 'up', label: 'Up' }, { value: 'binary', label: 'Binary' }]} /></div>
              </div>
              {/* ASP #13: a SMART goal is a sentence or two — multi-line, like the consultation note.
                  No maxLength: goal_statement is NVARCHAR(MAX) and the server sets no cap. */}
              <div style={{ ...fld, marginTop: 8 }}><label style={lab} htmlFor={`goal-stmt-${g.key}`}>Goal statement</label>
                <textarea id={`goal-stmt-${g.key}`} value={g.goal_statement} onChange={e => setGoal(i, { goal_statement: e.target.value })}
                  placeholder="SMART goal" rows={2} style={{ ...area, minHeight: 44 }} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 8, marginTop: 8 }}>
                <div style={fld}><label style={lab}>Measure</label><input aria-label="Measure" value={g.measure} maxLength={GOAL_FIELD_LIMITS.measure} onChange={e => setGoal(i, { measure: e.target.value })} style={boxFor('measure', g.measure)} />{counter('measure', g.measure)}</div>
                <div style={fld}><label style={lab}>Baseline{isBaselineExempt(g) ? ' (not required)' : ''}</label><input value={g.baseline_value} maxLength={GOAL_FIELD_LIMITS.baseline_value} onChange={e => setGoal(i, { baseline_value: e.target.value })} style={boxFor('baseline_value', g.baseline_value)} />{counter('baseline_value', g.baseline_value)}</div>
                <div style={fld}><label style={lab}>Baseline date</label><DateInput label="Baseline date" value={g.baseline_date} onChange={v => setGoal(i, { baseline_date: v ?? '' })} style={inp} /></div>
                <div style={fld}><label style={lab}>Target</label><input value={g.target_value} maxLength={GOAL_FIELD_LIMITS.target_value} onChange={e => setGoal(i, { target_value: e.target.value })} style={boxFor('target_value', g.target_value)} />{counter('target_value', g.target_value)}</div>
                <div style={fld}><label style={lab}>Target date</label><DateInput label="Target date" value={g.target_date} onChange={v => setGoal(i, { target_date: v ?? '' })} style={inp} offsetHelper={{}} /></div>
              </div>
              {/* #14: a lab-measurable goal with no baseline can be waived, with a reason, instead of
                  hard-stopping the plan. The reason is sent with the save and audited. */}
              {hasDataGap(g) && (effectiveWaiver(g, waivers)
                ? <div style={{ marginTop: 8, fontSize: 11.5, color: '#41566a', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <WaiverLine w={effectiveWaiver(g, waivers)!} />
                    <button onClick={() => setWaivers(w => { const n = { ...w }; delete n[g.key]; return n })}
                      style={{ ...inp, cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>Undo waiver</button>
                  </div>
                : <div style={{ marginTop: 8, fontSize: 11.5, color: '#b23a48', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>No baseline — required for a lab-measured goal.</span>
                    <button onClick={() => setWaiveFor(g.key)}
                      style={{ ...inp, cursor: 'pointer', fontSize: 11, padding: '2px 8px' }}>Waive baseline</button>
                  </div>)}
            </div>
          ))}
          <ConfirmDialog open={waiveFor !== null} title="Waive the baseline for this goal?"
            body="The goal is saved without a starting value. Your reason is recorded with the care plan."
            confirmLabel="Waive baseline" requireReason={{ label: 'Reason for waiving (required)' }}
            onConfirm={({ reason }) => { const k = waiveFor; if (k) setWaivers(w => ({ ...w, [k]: { reason: reason.trim(), persisted: false } })); setWaiveFor(null) }}
            onCancel={() => setWaiveFor(null)} />

          <button onClick={addGoal} style={{ ...inp, cursor: 'pointer' }}>+ Add goal</button>

          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 8 }}>
              <input type="checkbox" checked={attested} onChange={e => setAttested(e.target.checked)} />
              I attest this care plan reflects my clinical judgment.
            </label>
            <button onClick={save} disabled={gapBlocked || !!planBlocker()} title={planBlocker() ?? undefined}
              style={{ ...btnP, opacity: (gapBlocked || planBlocker()) ? 0.5 : 1, cursor: (gapBlocked || planBlocker()) ? 'not-allowed' : 'pointer' }}>Save care plan</button>
            {dirty && <UnsavedHint />}
            {msg && <span role={msg.kind === 'error' ? 'alert' : undefined} style={{ marginLeft: 10, fontSize: 12, color: msgColor(msg) }}>{msg.text}</span>}
            {!msg && dirty && draftSavedAt && <span style={{ marginLeft: 10, fontSize: 11, color: '#8496a5' }}>Draft kept in this tab at {fmtTime(draftSavedAt)}; not yet saved to the care plan.</span>}
          </div>
        </>
      )}
    </section>
  )
}

/** onDirtyChange (#8): true while either panel holds unsaved edits — the host confirms its Back with it.
 *  hideAssignee: the host renders the pharmacist picker itself (a decided review's locked fieldset would disable it). */
export default function CareRxCarePlanPanels({ therapyId, consultationNeeded, onDirtyChange, hideAssignee = false }:
  { therapyId: string; consultationNeeded: boolean | null; onDirtyChange?: (dirty: boolean) => void; hideAssignee?: boolean }) {
  const [consultDirty, setConsultDirty] = useState(false)
  const [planDirty, setPlanDirty] = useState(false)
  const dirty = consultDirty || planDirty
  useUnsavedChangesGuard(dirty)
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])
  return (
    <>
      <ConsultationPanel therapyId={therapyId} needed={consultationNeeded} onDirty={setConsultDirty} />
      <CarePlanPanel therapyId={therapyId} onDirty={setPlanDirty} hideAssignee={hideAssignee} />
    </>
  )
}
